import type Fuse from "fuse.js";

import {
    indexedDataToSearchList,
    type SearchData,
} from "@hsl/fund-explorer/search";
import { type TopLevelDataObj } from "@hsl/fund-explorer/types";

import { sortFundsByDescriptor } from "../../store/helpers";
import { type FundFinderStore } from "./types";

export function searchHelper(
    state: FundFinderStore,
    searchTerm: string | undefined,
    reset: boolean,
) {
    const term = reset
        ? ""
        : typeof searchTerm === "string"
          ? searchTerm
          : state.search.term;
    const isNum = !isNaN(Number(term));
    const isActiveSearch = term.length > 0;
    const scopeCodeOnly = term.length < 3;
    const searchOn = scopeCodeOnly ? ["scopeCode"] : state.search.searchOn;
    const terms = term.split(" ");
    const query = {
        $or: searchOn.map((prop) => ({
            $and: terms.map((t) => ({
                $or: [`'${t}`, t].map((_t) => ({ [prop]: _t })),
            })),
        })),
    };

    const list = indexedDataToSearchList(
        state
            .indexedData!.map((iData) => iData.data)
            .reduce((acc, data) => ({ ...acc, ...data }), {}),
    );
    const exactMatch = (value: string, token: string) =>
        value.toLowerCase() === token.toLowerCase();
    const simpleMatch = (value: string, token: string) =>
        value.toLowerCase().includes(token.toLowerCase());
    const simpleIndex = (value: string, token: string) =>
        value.toLowerCase().indexOf(token.toLowerCase());
    const toFuseResultMatch = (item: SearchData) => ({
        id: item.id,
        matches: searchOn
            .filter((prop) =>
                terms.some((t) => simpleMatch(`${item[prop]}`, t)),
            )
            .map((prop) => ({
                indices: [term, ...terms]
                    .filter((t) => simpleMatch(`${item[prop]}`, t))
                    .map((t) => [
                        simpleIndex(`${item[prop]}`, t),
                        simpleIndex(`${item[prop]}`, t) + t.length - 1,
                    ]),
                value: item[prop],
                key: prop,
            })) as readonly Fuse.FuseResultMatch[],
    });
    const simpleExactResults = isActiveSearch
        ? list
              .filter((item) =>
                  searchOn.some((prop) => exactMatch(`${item[prop]}`, term)),
              )
              .map(toFuseResultMatch)
        : [];
    const simpleResults =
        isActiveSearch && !scopeCodeOnly
            ? list
                  .filter((item) =>
                      terms.every((t) =>
                          searchOn.some((prop) =>
                              simpleMatch(`${item[prop]}`, t),
                          ),
                      ),
                  )
                  .map(toFuseResultMatch)
            : simpleExactResults;
    const rawResults =
        isActiveSearch && simpleResults.length === 0 && !isNum && !scopeCodeOnly
            ? state.search.searchFn(query)
            : simpleResults;
    const searchResults = isActiveSearch
        ? hoistResultsToFundLevel(state.getDataById, rawResults)
        : undefined;

    return {
        term,
        isActiveSearch,
        searchResults,
        rawResults,
    };
}

type RawResult = FundFinderStore["search"]["rawResults"][0];

export function hoistResultsToFundLevel(
    getter: <T = TopLevelDataObj>(id: string) => T | undefined,
    matches: RawResult[],
) {
    if (!matches || matches.length === 0) {
        return undefined;
    }

    const sortedFunds = sortFundsByDescriptor(
        getter,
        matches.map((m) => m.id),
    );
    const sortedFundResults = sortedFunds.reduce(
        (sf, id) => ({ ...sf, [id]: [] }),
        {} as Record<string, Fuse.FuseResultMatch[]>,
    );
    return matches.reduce((funds, match) => {
        const f = findFund(getter(match.id));
        if (!f) {
            return funds;
        }
        const matches = funds[f.localId];
        return {
            ...funds,
            [f.localId]: matches
                ? [...matches, ...(match.matches ?? [])]
                : [...(match.matches ?? [])],
        };
    }, sortedFundResults);

    function findFund(
        obj: TopLevelDataObj | undefined,
    ): TopLevelDataObj | undefined {
        if (!obj) {
            return;
        }
        switch (obj.type) {
            case "fund":
                return obj;
            case "shareclass":
            case "listing":
                return obj.parentId
                    ? findFund(getter(obj.parentId))
                    : undefined;
            default:
                return undefined;
        }
    }
}
